import { accountLabel, accountNumberWithCurrency } from './accountLabel';
import {
  Box,
  Button,
  Flex,
  Heading,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Table,
  Collapse,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  Badge,
  Checkbox,
  Tag,
  IconButton,
} from '@chakra-ui/react';
import { getBankTransactionsApi } from 'api/bankingApi';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import Card from 'components/card/Card';
import AccountBalanceCharts from './AccountBalanceCharts';
import { TwDatePicker } from 'components/DatePicker';
import FieldInput from 'components/fields/FieldInput';
import moment from 'moment';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from '@chakra-ui/icons';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const BankTransactions = () => {
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(
    moment().subtract(90, 'days').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [tempSelectedAccountIds, setTempSelectedAccountIds] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState(null);
  const [hasInitializedAccounts, setHasInitializedAccounts] = useState(false);
  const [transactionsOpen, setTransactionsOpen] = useState(false);
  const [pendingTransactionsOpen, setPendingTransactionsOpen] = useState(false);
  const { customerId } = useParams();
  const toast = useToast();

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const fetchBankTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBankTransactionsApi({
        customerId,
        startDate,
        endDate,
        accountIds: selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
        page,
        pageSize,
      });
      setData(response);

      // On first load, select all accounts by default
      if (
        !hasInitializedAccounts &&
        selectedAccountIds.length === 0 &&
        response?.accounts?.length > 0
      ) {
        const allAccountIds = response.accounts.map((acc) => acc.id);
        setSelectedAccountIds(allAccountIds);
        setTempSelectedAccountIds(allAccountIds);
        setHasInitializedAccounts(true);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch bank transactions',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [
    customerId,
    startDate,
    endDate,
    selectedAccountIds,
    page,
    pageSize,
    hasInitializedAccounts,
    toast,
  ]);

  useEffect(() => {
    fetchBankTransactions();
  }, [fetchBankTransactions]);

  const toggleAccount = (accountId) => {
    setTempSelectedAccountIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const selectAllAccounts = () => {
    if (data?.accounts) {
      setTempSelectedAccountIds(data.accounts.map((acc) => acc.id));
    }
  };

  const clearAllAccounts = () => {
    setTempSelectedAccountIds([]);
  };

  const applyAccountFilter = () => {
    setSelectedAccountIds(tempSelectedAccountIds);
    setPage(1);
  };

  const hasFilterChanges = () => {
    if (tempSelectedAccountIds.length !== selectedAccountIds.length) return true;
    return !tempSelectedAccountIds.every((id) => selectedAccountIds.includes(id));
  };

  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null) {
      return null;
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Rows in this table come either from a live bank feed or from a statement
  // someone emailed us, and the two do not carry the same confidence. Showing the
  // source means a questioned figure can be traced back to a parsed document
  // rather than silently reading as bank-verified.
  const getSourceBadge = (source) =>
    source === 'manual' ? (
      <Badge colorScheme="orange" variant="subtle">
        Statement
      </Badge>
    ) : (
      <Badge colorScheme="green" variant="subtle">
        Bank feed
      </Badge>
    );

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.95) {
      return (
        <Badge colorScheme="green" variant="subtle">
          {Math.round(confidence * 100)}%
        </Badge>
      );
    }
    if (confidence >= 0.85) {
      return (
        <Badge colorScheme="yellow" variant="subtle">
          {Math.round(confidence * 100)}%
        </Badge>
      );
    }
    return (
      <Badge colorScheme="red" variant="subtle">
        {Math.round(confidence * 100)}%
      </Badge>
    );
  };

  const mapConfidenceStringToFloat = (confidenceStr) => {
    if (!confidenceStr) return 0.5;
    const val = String(confidenceStr).toLowerCase().trim();
    if (val === 'high' || val === 'very_high' || val === 'very high') return 0.9;
    if (val === 'medium' || val === 'medium_high' || val === 'medium high') return 0.7;
    if (val === 'low' || val === 'low_medium' || val === 'low medium') return 0.3;
    if (val === 'very_low' || val === 'very low') return 0.1;
    return 0.5;
  };

  const downloadCSV = async () => {
    // Ensure we have pagination info or at least current transactions
    const currentTransactions = data?.transactions || [];
    const total =
      data?.pagination?.total != null
        ? data.pagination.total
        : currentTransactions.length;

    if (!total || total === 0) {
      toast({
        title: 'No data',
        description: 'No transactions to download',
        status: 'warning',
      });
      return;
    }

    let allTransactions = currentTransactions;

    // If there are more transactions than currently loaded, fetch all from the API
    if (data?.pagination && total > currentTransactions.length) {
      try {
        const fullResponse = await getBankTransactionsApi({
          customerId,
          startDate,
          endDate,
          accountIds:
            selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
          page: 1,
          pageSize: total,
        });
        allTransactions = fullResponse.transactions || currentTransactions;
      } catch (error) {
        // Fallback to current page if full fetch fails
        toast({
          title: 'Partial export',
          description:
            'Failed to fetch all transactions. Exporting current page only.',
          status: 'warning',
        });
      }
    }

    const headers = [
      'Description',
      'Amount',
      'Balance',
      'Date',
      'Merchant',
      'Category',
      'Confidence',
      'Account Number',
      'Account Name',
      'Bank',
        'Source',
      'Transaction ID',
    ];

    const csvRows = [
      headers.join(','),
      ...allTransactions.map((txn) => {
        const formattedDate =
          txn.date && txn.date.year && txn.date.month && txn.date.day
            ? moment([
                txn.date.year,
                txn.date.month - 1,
                txn.date.day,
              ]).format('YYYY-MM-DD')
            : '';
        return [
          `"${(txn.description || '').replace(/"/g, '""')}"`,
          txn.amount || 0,
          txn.balance || '',
          formattedDate,
          `"${(txn.merchant || '').replace(/"/g, '""')}"`,
          `"${(txn.category || '').replace(/"/g, '""')}"`,
          txn.confidence || 0,
          accountNumberWithCurrency(txn.account_number, txn.currency_code),
          `"${(txn.account_name || '').replace(/"/g, '""')}"`,
          `"${(txn.bank || '').replace(/"/g, '""')}"`,
            txn.source === 'manual' ? 'Statement' : 'Bank feed',
          txn.id || '',
        ].join(',');
      }),
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `bank_transactions_${moment().format('YYYY-MM-DD')}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredAccounts = useMemo(() => {
    if (!data?.accounts) return [];
    return data.accounts.filter((acc) => selectedAccountIds.includes(acc.id));
  }, [data?.accounts, selectedAccountIds]);

  const pendingTransactions =
    data?.pending_transactions || data?.pendingTransactions || [];

  const totalPages = data?.pagination?.total
    ? Math.ceil(data.pagination.total / pageSize)
    : 0;

  return (
    <OverlaySpinnerWrapper show={loading} isFixed={true}>
      <Box minW={'container.lg'} maxW={'max'}>
        {/* Filters */}
        <Flex w={'100%'} justify={'space-between'} alignItems={'end'} mb={35}>
          <Flex gap={5} flexWrap="wrap">
            <Box w={200}>
              <FieldInput label={'Start Date'} id={'startDate'}>
                <TwDatePicker
                  label={'Start Date'}
                  variant="white"
                  onChange={setStartDate}
                  value={startDate}
                  maxDate={endDate}
                  inputProps={{ boxShadow: 'box', placeholder: 'Start Date' }}
                />
              </FieldInput>
            </Box>
            <Box w={200}>
              <FieldInput label={'End Date'} id={'endDate'}>
                <TwDatePicker
                  label={'End Date'}
                  variant="white"
                  onChange={setEndDate}
                  value={endDate}
                  minDate={startDate}
                  maxDate={moment().format('YYYY-MM-DD')}
                  inputProps={{ boxShadow: 'box', placeholder: 'End Date' }}
                />
              </FieldInput>
            </Box>

            {/* Account Filter */}
            {data?.accounts && data.accounts.length > 0 && (
              <Box w={280}>
                <FieldInput label={'Accounts'} id={'accounts'}>
                  <Menu closeOnSelect={false}>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      w="100%"
                      boxShadow="box"
                      textAlign="left"
                    >
                      {tempSelectedAccountIds.length === 0
                        ? 'Select Accounts'
                        : tempSelectedAccountIds.length === data.accounts.length
                        ? 'All Accounts'
                        : `${tempSelectedAccountIds.length} Account${
                            tempSelectedAccountIds.length > 1 ? 's' : ''
                          } Selected`}
                    </MenuButton>
                    <MenuList maxH="300px" overflowY="auto">
                      <MenuItem onClick={selectAllAccounts}>Select All</MenuItem>
                      <MenuItem onClick={clearAllAccounts}>Clear</MenuItem>
                      <MenuDivider />
                      {data.accounts.map((account) => (
                        <MenuItem
                          key={account.id}
                          onClick={() => toggleAccount(account.id)}
                          closeOnSelect={false}
                        >
                          <Checkbox
                            isChecked={tempSelectedAccountIds.includes(account.id)}
                            mr={2}
                            readOnly
                            pointerEvents="none"
                          />
                          <Text>
                            {accountLabel(account)} - {account.account_name}
                          </Text>
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                </FieldInput>
              </Box>
            )}

            {/* Apply Button */}
            {hasFilterChanges() && (
              <Box>
                <FieldInput label={'\u00A0'} id={'apply'}>
                  <Button
                    colorScheme="blue"
                    onClick={applyAccountFilter}
                    boxShadow="box"
                  >
                    Apply Filter
                  </Button>
                </FieldInput>
              </Box>
            )}

            {/* Selected account tags */}
            {selectedAccountIds.length > 0 &&
              selectedAccountIds.length < data?.accounts?.length && (
                <Flex gap={2} flexWrap="wrap" alignItems="center">
                  {selectedAccountIds.map((id) => {
                    const account = data.accounts.find((a) => a.id === id);
                    if (!account) return null;
                    return (
                      <Tag
                        key={id}
                        size="md"
                        variant="subtle"
                        colorScheme="blue"
                        cursor="pointer"
                        onClick={() => {
                          const newSelection = selectedAccountIds.filter((accId) => accId !== id);
                          setSelectedAccountIds(newSelection);
                          setTempSelectedAccountIds(newSelection);
                          setPage(1);
                        }}
                      >
                        {accountLabel(account)}
                      </Tag>
                    );
                  })}
                </Flex>
              )}
          </Flex>
        </Flex>

        {data && (
          <>
            {/* Account Balances Table */}
            {filteredAccounts.length > 0 && (
              <Card mt={10} mb={6}>
                <Heading variant={'sectionHeader'} borderColor={'twelve.green.300'}>
                  Account Balances
                </Heading>
                <Box mx={-5} mt={10}>
                  <TableContainer>
                    <Table variant={'simple'} borderColor={borderColor}>
                      <Thead>
                        <Tr>
                          <Th borderColor={borderColor}>Bank</Th>
                          <Th borderColor={borderColor}>Account Name</Th>
                          <Th borderColor={borderColor}>Account Number</Th>
                          <Th borderColor={borderColor} isNumeric>
                            Current Balance
                          </Th>
                          <Th borderColor={borderColor} isNumeric>
                            Available Balance
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filteredAccounts.map((account) => (
                          <Tr key={account.id}>
                            <Td borderColor={borderColor}>{account.bank_name}</Td>
                            <Td borderColor={borderColor}>{account.account_name}</Td>
                            <Td borderColor={borderColor}>
                              {accountNumberWithCurrency(account.last_four, account.currency_code)}
                            </Td>
                            <Td borderColor={borderColor} isNumeric>
                              {formatCurrency(account.current_balance)}
                            </Td>
                            <Td borderColor={borderColor} isNumeric>
                              <Text color="green.500">
                                {formatCurrency(account.available_balance)}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </Card>
            )}

            <AccountBalanceCharts
              dailyBalances={data?.daily_balances || data?.dailyBalances || []}
              selectedAccountIds={selectedAccountIds}
              formatCurrency={formatCurrency}
            />

            {/* Transactions Table */}
            <Card mt={10}>
              <Flex justify="space-between" align="center" mb={transactionsOpen ? 4 : 0}>
                <Flex
                  align="center"
                  cursor="pointer"
                  userSelect="none"
                  w="fit-content"
                  role="button"
                  tabIndex={0}
                  aria-expanded={transactionsOpen}
                  onClick={() => setTransactionsOpen((open) => !open)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setTransactionsOpen((open) => !open);
                    }
                  }}
                >
                  {transactionsOpen ? (
                    <ChevronDownIcon boxSize={5} color="gray.500" mr={1} />
                  ) : (
                    <ChevronRightIcon boxSize={5} color="gray.500" mr={1} />
                  )}
                  <Heading variant={'sectionHeader'} borderColor={'twelve.green.300'}>
                    Transactions
                  </Heading>
                </Flex>
                <Button
                  leftIcon={<DownloadIcon />}
                  size="sm"
                  variant="outline"
                  onClick={downloadCSV}
                >
                  Download CSV
                </Button>
              </Flex>
              <Collapse in={transactionsOpen} animateOpacity unmountOnExit>
              <Box mx={-5} mt={10}>
                <TableContainer maxH="600px" overflowY="auto">
                  <Table variant={'simple'} borderColor={borderColor} size="sm">
                    <Thead position="sticky" top={0} bg="white" zIndex={1}>
                      <Tr>
                        <Th borderColor={borderColor}>Description</Th>
                        <Th 
                          borderColor={borderColor} 
                          isNumeric
                        >
                          Amount
                        </Th>
                        <Th borderColor={borderColor} isNumeric>
                          Balance
                        </Th>
                        <Th 
                          borderColor={borderColor}
                        >
                          Date
                        </Th>
                        <Th borderColor={borderColor}>Merchant</Th>
                        <Th borderColor={borderColor}>Category</Th>
                        <Th borderColor={borderColor} textAlign="center">
                          Confidence
                        </Th>
                        <Th borderColor={borderColor}>Account #</Th>
                        <Th borderColor={borderColor}>Account Name</Th>
                        <Th borderColor={borderColor}>Bank</Th>
                        <Th borderColor={borderColor}>Source</Th>
                        <Th borderColor={borderColor}>Transaction ID</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data?.transactions && data.transactions.length > 0 ? (
                        data.transactions.map((txn) => (
                          <Tr key={txn.id}>
                            <Td borderColor={borderColor}>{txn.description}</Td>
                            <Td borderColor={borderColor} isNumeric>
                              <Text
                                color={
                                  (txn.amount || 0) >= 0 ? 'green.500' : 'red.500'
                                }
                                fontWeight="medium"
                              >
                                {(txn.amount || 0) >= 0 ? '+' : ''}
                                {formatCurrency(txn.amount || 0)}
                              </Text>
                            </Td>
                            <Td borderColor={borderColor} isNumeric>
                              {txn.balance !== null && txn.balance !== undefined ? (
                                <Text
                                  color={
                                    (txn.balance || 0) >= 0 ? 'green.500' : 'red.500'
                                  }
                                  fontWeight="medium"
                                >
                                  {formatCurrency(txn.balance)}
                                </Text>
                              ) : (
                                '-'
                              )}
                            </Td>
                            <Td borderColor={borderColor}>
                              {txn.date && txn.date.year && txn.date.month && txn.date.day
                                ? moment([txn.date.year, txn.date.month - 1, txn.date.day]).format('MMM DD, YYYY')
                                : '-'}
                            </Td>
                            <Td borderColor={borderColor}>{txn.merchant || '-'}</Td>
                            <Td borderColor={borderColor}>
                              <Badge variant="outline">{txn.category || 'Other'}</Badge>
                            </Td>
                            <Td borderColor={borderColor} textAlign="center">
                              {txn.confidence !== undefined
                                ? getConfidenceBadge(txn.confidence)
                                : '-'}
                            </Td>
                            <Td borderColor={borderColor}>
                              {accountNumberWithCurrency(txn.account_number, txn.currency_code)}
                            </Td>
                            <Td borderColor={borderColor}>{txn.account_name || '-'}</Td>
                            <Td borderColor={borderColor}>{txn.bank || '-'}</Td>
                            <Td borderColor={borderColor}>{getSourceBadge(txn.source)}</Td>
                            <Td borderColor={borderColor}>
                              <Text fontSize="xs" fontFamily="mono">
                                {txn.id || '-'}
                              </Text>
                            </Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td colSpan={12} textAlign="center" py={8}>
                            <Text color="gray.500">
                              No transactions found for the selected filters
                            </Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Pagination */}
              {data.pagination && data.pagination.total > 0 && (
                <Flex
                  justify="space-between"
                  align="center"
                  mt={4}
                  pt={4}
                  borderTop="1px"
                  borderColor={borderColor}
                >
                  <Flex align="center" gap={2}>
                    <Text fontSize="sm" color="gray.500">
                      Show rows per page
                    </Text>
                    <Menu>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        size="sm"
                        variant="outline"
                      >
                        {pageSize}
                      </MenuButton>
                      <MenuList>
                        {ROWS_PER_PAGE_OPTIONS.map((option) => (
                          <MenuItem
                            key={option}
                            onClick={() => {
                              setPageSize(option);
                              setPage(1);
                            }}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </Flex>

                  <Flex align="center" gap={4}>
                    <Text fontSize="sm" color="gray.500">
                      {data.pagination.page * pageSize - pageSize + 1} –{' '}
                      {Math.min(data.pagination.page * pageSize, data.pagination.total)} of{' '}
                      {data.pagination.total}
                    </Text>
                    <Flex gap={1}>
                      <IconButton
                        icon={<ChevronLeftIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        isDisabled={page === 1}
                        aria-label="Previous page"
                      />
                      <IconButton
                        icon={<ChevronRightIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        isDisabled={page >= totalPages}
                        aria-label="Next page"
                      />
                    </Flex>
                  </Flex>
                </Flex>
              )}
              </Collapse>
            </Card>

            {/* Pending Transactions Table */}
            {pendingTransactions.length > 0 && (
                <Card mt={10}>
                  <Flex
                    align="center"
                    cursor="pointer"
                    userSelect="none"
                    w="fit-content"
                    role="button"
                    tabIndex={0}
                    aria-expanded={pendingTransactionsOpen}
                    onClick={() => setPendingTransactionsOpen((open) => !open)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setPendingTransactionsOpen((open) => !open);
                      }
                    }}
                  >
                    {pendingTransactionsOpen ? (
                      <ChevronDownIcon boxSize={5} color="gray.500" mr={1} />
                    ) : (
                      <ChevronRightIcon boxSize={5} color="gray.500" mr={1} />
                    )}
                    <Heading variant={'sectionHeader'} borderColor={'twelve.green.300'}>
                      Pending Transactions
                    </Heading>
                  </Flex>
                  <Collapse in={pendingTransactionsOpen} animateOpacity unmountOnExit>
                    <Box mx={-5} mt={10}>
                    <TableContainer maxH="400px" overflowY="auto">
                      <Table variant={'simple'} borderColor={borderColor} size="sm">
                        <Thead position="sticky" top={0} bg="white" zIndex={1}>
                          <Tr>
                            <Th borderColor={borderColor}>Description</Th>
                            <Th borderColor={borderColor} isNumeric>
                              Amount
                            </Th>
                            <Th borderColor={borderColor}>
                              Date
                            </Th>
                            <Th borderColor={borderColor}>Merchant</Th>
                            <Th borderColor={borderColor}>Category</Th>
                            <Th borderColor={borderColor} textAlign="center">
                              Confidence
                            </Th>
                            <Th borderColor={borderColor}>Account #</Th>
                            <Th borderColor={borderColor}>Account Name</Th>
                            <Th borderColor={borderColor}>Bank</Th>
                            <Th borderColor={borderColor}>Account Type</Th>
                            <Th borderColor={borderColor}>Currency</Th>
                            <Th borderColor={borderColor}>Source</Th>
                            <Th borderColor={borderColor}>Transaction ID</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {pendingTransactions.map((txn) => (
                          <Tr key={txn.id}>
                            <Td borderColor={borderColor}>{txn.description}</Td>
                            <Td borderColor={borderColor} isNumeric>
                              <Text
                                color={
                                  (txn.amount || 0) >= 0 ? 'green.500' : 'red.500'
                                }
                                fontWeight="medium"
                              >
                                {(txn.amount || 0) >= 0 ? '+' : ''}
                                {formatCurrency(txn.amount || 0)}
                              </Text>
                            </Td>
                            <Td borderColor={borderColor}>
                              {txn.transaction_date
                                ? moment(txn.transaction_date).format('MMM DD, YYYY')
                                : '-'}
                            </Td>
                            <Td borderColor={borderColor}>{txn.merchant || '-'}</Td>
                            <Td borderColor={borderColor}>
                              <Badge variant="outline">{txn.category || 'Other'}</Badge>
                            </Td>
                            <Td borderColor={borderColor} textAlign="center">
                              {txn.confidence !== undefined && txn.confidence !== null
                                ? getConfidenceBadge(
                                    typeof txn.confidence === 'number'
                                      ? txn.confidence
                                      : mapConfidenceStringToFloat(txn.confidence)
                                  )
                                : '-'}
                            </Td>
                            <Td borderColor={borderColor}>
                              {accountNumberWithCurrency(txn.account_number, txn.currency_code)}
                            </Td>
                            <Td borderColor={borderColor}>{txn.account_name || '-'}</Td>
                            <Td borderColor={borderColor}>{txn.bank || '-'}</Td>
                            <Td borderColor={borderColor}>{txn.account_type || '-'}</Td>
                            <Td borderColor={borderColor}>{txn.currency_code || '-'}</Td>
                            <Td borderColor={borderColor}>{getSourceBadge(txn.source)}</Td>
                            <Td borderColor={borderColor}>
                              <Text fontSize="xs" fontFamily="mono">
                                {txn.id || '-'}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                    </TableContainer>
                    </Box>
                  </Collapse>
                </Card>
            )}
          </>
        )}
      </Box>
    </OverlaySpinnerWrapper>
  );
};

BankTransactions.propTypes = {};

export default BankTransactions;
